/*eslint max-lines-per-function: ["error", 260]*/
import React, { useMemo } from 'react';
import { Button, Card, Tab } from 'react-bootstrap';

import { usePokCore } from '../../../common/hooks/usePokCore';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { ContractSearch } from '../../components/Contracts/ContractSearch';
import { TabsHistory } from '../../../app/components/TabsHistory';
import { useNavigation } from '../../../common/navigation';
import { GridGetterFunction } from '../../../common/components/Grid/GridDataTypes';

interface ContractsViewProps {
  tv: boolean;
  withAddButton: boolean;
}

export const ContractsView: React.FC<ContractsViewProps> = props => {
  const pok = usePokCore();
  const nav = useNavigation();
  const { t, tk } = useTranslation('projectLng');

  const handleClick = async (id?: string, newWindow?: boolean) => {
    if (props.tv) {
      nav.contractEditorTV(id, newWindow);
    } else {
      nav.contractEditor(id, newWindow);
    }
  };

  const handleAdd = async () => {
    if (props.tv) {
      nav.contractEditorTV(undefined, false);
    } else {
      nav.contractEditor(undefined, false);
    }
  };

  const getDataPending = useMemo(
    () =>
      ((
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ) =>
        pok.contracts.getPendingByPortion(
          t,
          tk,
          props.tv,
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          filterColumns,
        )) as GridGetterFunction,
    [pok.contracts, t, tk, props.tv],
  );

  const getDataWaiting = useMemo(
    () =>
      ((
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ) =>
        pok.contracts.getWaitingForApprovalByPortion(
          t,
          tk,
          props.tv,
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          filterColumns,
        )) as GridGetterFunction,
    [pok.contracts, t, tk, props.tv],
  );

  const getDataApproved = useMemo(
    () =>
      ((
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ) =>
        pok.contracts.getApprovedNotSentByPortion(
          t,
          tk,
          props.tv,
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          filterColumns,
        )) as GridGetterFunction,
    [pok.contracts, t, tk, props.tv],
  );

  const getDataAll = useMemo(
    () =>
      ((
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ) =>
        pok.contracts.getAllByPortion(
          t,
          tk,
          props.tv,
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          filterColumns,
        )) as GridGetterFunction,
    [pok.contracts, t, tk, props.tv],
  );

  const getDataUnsigned = useMemo(
    () =>
      ((
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ) =>
        pok.contracts.getUnsignedByPortion(
          t,
          tk,
          props.tv,
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          filterColumns,
        )) as GridGetterFunction,
    [pok.contracts, t, tk, props.tv],
  );

  return (
    <Card className="main-card">
      <Card.Body className="m-3">
        <Card.Title className="pb-3">{t(tk.contract.contracts)}</Card.Title>
        {props.withAddButton && (
          <Button
            variant="outline-primary"
            onClick={handleAdd}
            className="mb-4"
          >
            {props.tv
              ? t(tk.contract.contractNewTvFromFile)
              : t(tk.contract.contractNewFromFile)}
          </Button>
        )}
        <TabsHistory
          activeKey="pending_contracts"
          id="pending_contracts"
          className="mb-3"
          mountOnEnter
          unmountOnExit
        >
          <Tab
            eventKey="pending_contracts"
            title={t(tk.contract.entered)}
            className="m-3"
          >
            <ContractSearch
              getData={getDataPending}
              getCount={(filterText, filterColumns) =>
                pok.contracts.getPendingCount(
                  props.tv,
                  filterText,
                  filterColumns,
                )
              }
              onRowClick={handleClick}
              displayAmountUnsettled={true}
            />
          </Tab>
          <Tab
            eventKey="unsigned_contracts"
            title={t(tk.contract.unsigned)}
            className="m-3"
          >
            <ContractSearch
              getData={getDataUnsigned}
              getCount={(filterText, filterColumns) =>
                pok.contracts.getUnsignedCount(
                  props.tv,
                  filterText,
                  filterColumns,
                )
              }
              onRowClick={handleClick}
              withUnsignDate={true}
            />
          </Tab>
          <Tab
            eventKey="waitingForApproval_contracts"
            title={t(tk.contract.waitingForSigning)}
            className="m-3"
          >
            <ContractSearch
              getData={getDataWaiting}
              getCount={(filterText, filterColumns) =>
                pok.contracts.getWaitingForApprovalCount(
                  props.tv,
                  filterText,
                  filterColumns,
                )
              }
              onRowClick={handleClick}
              withWaitingSignDate={true}
            />
          </Tab>
          <Tab
            eventKey="approval_contracts"
            title={t(tk.contract.signedNotSent)}
            className="m-3"
          >
            <ContractSearch
              getData={getDataApproved}
              getCount={(filterText, filterColumns) =>
                pok.contracts.getApprovedNotSentCount(
                  props.tv,
                  filterText,
                  filterColumns,
                )
              }
              onRowClick={handleClick}
              withSignDate={true}
            />
          </Tab>
          <Tab
            eventKey="all_contracts"
            title={t(tk.contract.all)}
            className="m-3"
          >
            <ContractSearch
              getData={getDataAll}
              getCount={(filterText, filterColumns) =>
                pok.contracts.getAllCount(props.tv, filterText, filterColumns)
              }
              onRowClick={handleClick}
              initialOrderDirection="DESC"
              displayStatus={true}
              withSignDate={true}
            />
          </Tab>
        </TabsHistory>
      </Card.Body>
    </Card>
  );
};
